"use client";

import { useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAddresses,
  syncGuestAddress,
  hydrateFromLocalStorage,
  setSelectedAddress,
} from "../../config/redux/slices/AddressSlice";
import { setLocation } from "../../config/redux/slices/LocationSlice";

/**
 * AddressProvider Component
 * 
 * This component handles:
 * 1. Hydrating address state from localStorage on mount
 * 2. Syncing guest addresses after login
 * 3. Auto-fetching addresses for authenticated users
 * 4. Setting default address on app load
 */
export default function AddressProvider({ children }) {
  const dispatch = useDispatch();
  const previouslyAuthenticated = useRef(false);

  // Redux state
  const isAuthenticated = useSelector((state) => !!state.auth.token);
  const addresses = useSelector((state) => state.address?.addresses || []);
  const selectedAddress = useSelector((state) => state.address?.selectedAddress);
  const locationState = useSelector((state) => state.location);

  // Initialize address state from localStorage on mount
  useEffect(() => {
    dispatch(hydrateFromLocalStorage());
  }, [dispatch]);

  // Handle authentication state changes
  useEffect(() => {
    if (isAuthenticated && !previouslyAuthenticated.current) {
      // User just logged in
      
      // 1. Sync guest address if exists
      dispatch(syncGuestAddress());
      
      // 2. Fetch user's addresses
      dispatch(fetchAddresses());
      
      previouslyAuthenticated.current = true;
    } else if (!isAuthenticated && previouslyAuthenticated.current) {
      // User just logged out
      previouslyAuthenticated.current = false;
    }
  }, [isAuthenticated, dispatch]);

  // Set up default address when addresses are loaded
  useEffect(() => {
    if (!isAuthenticated || addresses.length === 0) return;

    // Find default/primary address
    const defaultAddress = addresses.find((addr) => addr.primary);
    
    // If no selected address, use the default or first address
    if (!selectedAddress && !locationState.city) {
      const addressToUse = defaultAddress || addresses[0];
      
      if (addressToUse) {
        // Set in address slice
        dispatch(setSelectedAddress(addressToUse));
        
        // Set in location slice for backward compatibility
        dispatch(
          setLocation({
            city: addressToUse.city,
            latitude: addressToUse.latitude,
            longitude: addressToUse.longitude,
            addressLine1: addressToUse.addressLine1,
            addressLine2: addressToUse.addressLine2,
            state: addressToUse.state,
            country: addressToUse.country,
            postalCode: addressToUse.postalCode,
          })
        );
      }
    }
  }, [addresses, isAuthenticated, selectedAddress, locationState.city, dispatch]);

  // Keep location slice in sync with the currently selected address.
  // Header consumes location slice, while address selection updates address slice.
  useEffect(() => {
    if (!selectedAddress) return;

    dispatch(
      setLocation({
        city: selectedAddress.city || "",
        latitude: selectedAddress.latitude ?? null,
        longitude: selectedAddress.longitude ?? null,
        addressLine1: selectedAddress.addressLine1 || "",
        addressLine2: selectedAddress.addressLine2 || "",
        state: selectedAddress.state || "",
        country: selectedAddress.country || "",
        postalCode: selectedAddress.postalCode || "",
      })
    );
  }, [
    dispatch,
    selectedAddress?.city,
    selectedAddress?.latitude,
    selectedAddress?.longitude,
    selectedAddress?.addressLine1,
    selectedAddress?.addressLine2,
    selectedAddress?.state,
    selectedAddress?.country,
    selectedAddress?.postalCode,
  ]);

  // Listen for page visibility changes to refresh data
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        // Refresh addresses when page becomes visible again
        dispatch(fetchAddresses());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, dispatch]);

  // Handle network reconnection
  useEffect(() => {
    const handleOnline = () => {
      if (isAuthenticated) {
        dispatch(fetchAddresses());
      }
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [isAuthenticated, dispatch]);

  return <>{children}</>;
}

/**
 * Utility functions for guest address management
 */

// Save guest address to localStorage
export const saveGuestAddressToStorage = (address) => {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.setItem("guestAddress", JSON.stringify(address));
    localStorage.setItem("selectedAddress", JSON.stringify(address));
  } catch (error) {
    console.error("Failed to save guest address:", error);
  }
};

// Get guest address from localStorage
export const getGuestAddressFromStorage = () => {
  if (typeof window === "undefined") return null;
  
  try {
    const guestAddress = localStorage.getItem("guestAddress");
    return guestAddress ? JSON.parse(guestAddress) : null;
  } catch (error) {
    console.error("Failed to get guest address:", error);
    return null;
  }
};

// Clear guest address from localStorage
export const clearGuestAddressFromStorage = () => {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.removeItem("guestAddress");
  } catch (error) {
    console.error("Failed to clear guest address:", error);
  }
};

// Check if guest has an address
export const hasGuestAddress = () => {
  return !!getGuestAddressFromStorage();
};

// Merge guest address with backend after login
export const mergeGuestAddressAfterLogin = async (createAddressCallback) => {
  const guestAddress = getGuestAddressFromStorage();
  
  if (!guestAddress) return null;
  
  try {
    // Create the guest address in the backend
    const result = await createAddressCallback(guestAddress);
    
    // Clear the guest address from localStorage
    clearGuestAddressFromStorage();
    
    return result;
  } catch (error) {
    console.error("Failed to merge guest address:", error);
    return null;
  }
};
